/**
 * AdaptareTab - Conversie PDF stat de plată → CSV plată bancară
 */
import { memo, useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Settings,
  Upload,
  Download,
  FileText,
  CheckCircle2,
  AlertCircle,
  Save,
  Loader2,
  Check,
  Users,
  History,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileDown,
  Calculator,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS_RO_LIST = [
  { value: 1, label: 'Ianuarie' }, { value: 2, label: 'Februarie' }, { value: 3, label: 'Martie' },
  { value: 4, label: 'Aprilie' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Iunie' },
  { value: 7, label: 'Iulie' }, { value: 8, label: 'August' }, { value: 9, label: 'Septembrie' },
  { value: 10, label: 'Octombrie' }, { value: 11, label: 'Noiembrie' }, { value: 12, label: 'Decembrie' }
];

const LunaSelector = memo(function LunaSelector({ month, year, onMonthChange, onYearChange }) {
  const handlePrev = () => {
    if (month === 1) { onMonthChange(12); onYearChange(year - 1); }
    else onMonthChange(month - 1);
  };
  const handleNext = () => {
    if (month === 12) { onMonthChange(1); onYearChange(year + 1); }
    else onMonthChange(month + 1);
  };
  const handleCurrent = () => {
    onMonthChange(new Date().getMonth() + 1);
    onYearChange(new Date().getFullYear());
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant="outline" size="sm" onClick={handlePrev} data-testid="luna-prev">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
        <Select value={String(month)} onValueChange={(v) => onMonthChange(parseInt(v))}>
          <SelectTrigger className="w-[120px] h-8 border-0 bg-transparent text-sm font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS_RO_LIST.map(m => (
              <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => onYearChange(parseInt(v))}>
          <SelectTrigger className="w-[80px] h-8 border-0 bg-transparent text-sm font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" size="sm" onClick={handleNext} data-testid="luna-next">
        <ChevronRight className="w-4 h-4" />
      </Button>
      <Button variant="default" size="sm" onClick={handleCurrent} data-testid="luna-current">
        Luna curentă
      </Button>
    </div>
  );
});

// ============ Setări Adaptare Sub-component ============
const SetariAdaptare = memo(function SetariAdaptare({ token }) {
  const [settings, setSettings] = useState({
    banca_platitor: '',
    iban_platitor: '',
    nume_platitor: '',
    cod_fiscal_platitor: '',
    banca_beneficiar: '',
    data_plata: '',
    detalii_1_salarii: '',
    detalii_1_clauza: ''
  });
  const [employees, setEmployees] = useState([]);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingExcluded, setSavingExcluded] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [settingsRes, employeesRes, excludedRes] = await Promise.all([
          fetch(`${API_URL}/api/salary-adapter/settings`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/salary-adapter/employees-list`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/salary-adapter/excluded-employees`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSettings(data);
        }
        if (employeesRes.ok) {
          const data = await employeesRes.json();
          setEmployees(data.employees || []);
        }
        if (excludedRes.ok) {
          const data = await excludedRes.json();
          setExcludedIds(new Set(data.excluded_ids || []));
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      if (res.ok) toast.success('Setări salvate cu succes');
      else toast.error('Eroare la salvarea setărilor');
    } catch {
      toast.error('Eroare de conexiune');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEmployee = (empId) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(empId)) next.delete(empId);
      else next.add(empId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setExcludedIds(new Set());
  };

  const handleDeselectAll = () => {
    setExcludedIds(new Set(employees.map(e => e.id)));
  };

  const handleSaveExcluded = async () => {
    setSavingExcluded(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/excluded-employees`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ excluded_ids: Array.from(excludedIds) })
      });
      if (res.ok) toast.success('Lista angajați salvată');
      else toast.error('Eroare la salvare');
    } catch {
      toast.error('Eroare de conexiune');
    } finally {
      setSavingExcluded(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const includedCount = employees.length - excludedIds.size;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date Plătitor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Settings className="w-5 h-5 text-blue-600" />
            Setări Adaptare - Date Plătitor
          </CardTitle>
          <CardDescription>
            Configurează datele statice care se completează automat în fișierul CSV de plată bancară
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="banca_platitor">Banca Plătitor</Label>
              <Input id="banca_platitor" data-testid="settings-banca-platitor" placeholder="ex: BRDE"
                value={settings.banca_platitor} onChange={(e) => handleChange('banca_platitor', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="iban_platitor">IBAN Plătitor</Label>
              <Input id="iban_platitor" data-testid="settings-iban-platitor" placeholder="ex: RO56BRDE230SV39316032300"
                value={settings.iban_platitor} onChange={(e) => handleChange('iban_platitor', e.target.value.toUpperCase())} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nume_platitor">Nume Plătitor</Label>
              <Input id="nume_platitor" data-testid="settings-nume-platitor" placeholder="ex: CRIMACO TRP SRL"
                value={settings.nume_platitor} onChange={(e) => handleChange('nume_platitor', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cod_fiscal_platitor">Cod Fiscal Plătitor</Label>
              <Input id="cod_fiscal_platitor" data-testid="settings-cod-fiscal" placeholder="ex: 23686979"
                value={settings.cod_fiscal_platitor} onChange={(e) => handleChange('cod_fiscal_platitor', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="banca_beneficiar">Banca Beneficiar (implicită)</Label>
              <Input id="banca_beneficiar" data-testid="settings-banca-beneficiar" placeholder="ex: BRDE"
                value={settings.banca_beneficiar} onChange={(e) => handleChange('banca_beneficiar', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_plata">Data Plată</Label>
              <Input id="data_plata" data-testid="settings-data-plata" placeholder="ex: 16.02.2026"
                value={settings.data_plata} onChange={(e) => handleChange('data_plata', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="detalii_1_salarii">Detalii 1 (Stat de plată)</Label>
              <Input id="detalii_1_salarii" data-testid="settings-detalii-salarii" placeholder="ex: SALARII FEBRUARIE"
                value={settings.detalii_1_salarii} onChange={(e) => handleChange('detalii_1_salarii', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="detalii_1_clauza">Detalii 1 (Clauza mobilitate)</Label>
              <Input id="detalii_1_clauza" data-testid="settings-detalii-clauza" placeholder="ex: CLAUZA DE MOBILITATE FEBRUARIE"
                value={settings.detalii_1_clauza} onChange={(e) => handleChange('detalii_1_clauza', e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={handleSaveSettings} disabled={saving} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="save-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvează Setări
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Angajați incluși în export */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="w-5 h-5 text-blue-600" />
                Angajați incluși în Export CSV
              </CardTitle>
              <CardDescription>
                Debifează angajații care primesc salariul cash și nu trebuie incluși în fișierul CSV bancar
              </CardDescription>
            </div>
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-sm" data-testid="included-count">
              {includedCount} / {employees.length} incluși
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Select all / Deselect all */}
          <div className="flex items-center gap-3 mb-3">
            <Button variant="outline" size="sm" onClick={handleSelectAll} data-testid="select-all-btn">
              Bifează toți
            </Button>
            <Button variant="outline" size="sm" onClick={handleDeselectAll} data-testid="deselect-all-btn">
              Debifează toți
            </Button>
          </div>

          <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 sticky top-0">
                  <TableHead className="w-[50px] font-semibold text-center">CSV</TableHead>
                  <TableHead className="font-semibold">Nume Prenume</TableHead>
                  <TableHead className="font-semibold">CNP</TableHead>
                  <TableHead className="font-semibold">Cont Bancar (IBAN)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => {
                  const isIncluded = !excludedIds.has(emp.id);
                  return (
                    <TableRow
                      key={emp.id}
                      className={`cursor-pointer transition-colors ${!isIncluded ? 'bg-slate-100 opacity-60' : 'hover:bg-blue-50/50'}`}
                      onClick={() => handleToggleEmployee(emp.id)}
                      data-testid={`emp-row-${emp.id}`}
                    >
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isIncluded}
                          onCheckedChange={() => handleToggleEmployee(emp.id)}
                          data-testid={`emp-checkbox-${emp.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {emp.nume} {emp.prenume}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{emp.cnp || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {emp.cont_bancar ? (
                          <span className="text-green-700">{emp.cont_bancar}</span>
                        ) : (
                          <span className="text-amber-500">Lipsă IBAN</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveExcluded} disabled={savingExcluded} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="save-excluded-btn">
              {savingExcluded ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvează Lista
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

// ============ Inline IBAN Editor ============
function IBANInlineEdit({ index, employeeId, onSave }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    await onSave(index, employeeId, value);
    setSaving(false);
  };

  return (
    <div className="flex items-center gap-1">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value.toUpperCase())}
        placeholder="RO..."
        className="h-7 text-xs w-[180px] font-mono"
        data-testid={`iban-input-${index}`}
        onKeyDown={(e) => e.key === 'Enter' && handleSave()}
      />
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleSave}
        disabled={saving || !value.trim()} data-testid={`iban-save-${index}`}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 text-green-600" />}
      </Button>
    </div>
  );
}

// ============ Upload & Export Sub-component ============
const UploadStatPlata = memo(function UploadStatPlata({ token }) {
  const [entries, setEntries] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [pdfBase64, setPdfBase64] = useState('');
  const [pdfType, setPdfType] = useState('');
  const [luna, setLuna] = useState('');

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Selectați un fișier PDF');
      return;
    }
    setFileName(file.name);
    setUploading(true);
    setEntries([]);
    setPdfBase64('');
    setPdfType('');
    setLuna('');

    // Read file as base64 for later saving
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(',')[1];
      setPdfBase64(b64);
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/parse-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.employees || []);
        setPdfType(data.pdf_type || 'salarii');
        setLuna(data.luna || '');
        toast.success(`${data.total} angajați extrași din PDF (${data.pdf_type === 'tichete' ? 'Clauza mobilitate' : 'Salarii'} - ${data.luna || '?'})`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Eroare la parsarea PDF-ului');
      }
    } catch {
      toast.error('Eroare de conexiune la server');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, [token]);

  const handleExportCSV = useCallback(async () => {
    const validEntries = entries.filter(e => e.matched && e.amount);
    if (validEntries.length === 0) {
      toast.error('Nu există intrări valide pentru export');
      return;
    }
    setExporting(true);
    try {
      // 1. Generate CSV
      const res = await fetch(`${API_URL}/api/salary-adapter/generate-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entries, pdf_type: pdfType })
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const disposition = res.headers.get('content-disposition');
        const filenameMatch = disposition?.match(/filename=(.+)/);
        a.href = url;
        a.download = filenameMatch ? filenameMatch[1] : 'salarii_export.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        // 2. Auto-save to history
        if (pdfBase64 && luna) {
          try {
            await fetch(`${API_URL}/api/salary-adapter/exports`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                entries,
                pdf_type: pdfType,
                luna,
                pdf_base64: pdfBase64,
                pdf_filename: fileName
              })
            });
            toast.success('CSV exportat și salvat în Istoric Plăți');
          } catch {
            toast.success('CSV exportat, dar nu s-a salvat în istoric');
          }
        } else {
          toast.success('CSV exportat cu succes');
        }
      } else {
        toast.error('Eroare la generarea CSV-ului');
      }
    } catch {
      toast.error('Eroare de conexiune');
    } finally {
      setExporting(false);
    }
  }, [entries, token, pdfBase64, pdfType, luna, fileName]);

  const handleAmountChange = useCallback((index, newAmount) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], amount: newAmount ? parseInt(newAmount) : null };
      return updated;
    });
  }, []);

  const handleSaveIBAN = useCallback(async (index, employeeId, iban) => {
    if (!employeeId || !iban.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/update-iban`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ employee_id: employeeId, iban: iban.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], iban: data.iban };
          return updated;
        });
        toast.success('IBAN salvat');
      } else {
        toast.error('Eroare la salvarea IBAN-ului');
      }
    } catch {
      toast.error('Eroare de conexiune');
    }
  }, [token]);

  const matchedCount = entries.filter(e => e.matched).length;
  const unmatchedCount = entries.filter(e => !e.matched).length;
  const totalAmount = entries.reduce((sum, e) => sum + (e.matched && e.amount ? e.amount : 0), 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="w-5 h-5 text-blue-600" />
            Încarcă Stat de Plată
          </CardTitle>
          <CardDescription>
            Încarcă PDF-ul cu statul de plată. Sistemul va extrage automat numele angajaților și sumele de plată.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <label htmlFor="pdf-upload"
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
              data-testid="pdf-upload-area">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin text-blue-600" /> : <FileText className="w-5 h-5 text-blue-600" />}
              <span className="text-sm font-medium text-slate-700">
                {uploading ? 'Se procesează...' : fileName || 'Selectează PDF stat de plată'}
              </span>
              <input id="pdf-upload" type="file" accept=".pdf" className="hidden"
                onChange={handleUpload} disabled={uploading} data-testid="pdf-upload-input" />
            </label>
            {entries.length > 0 && (
              <Button onClick={handleExportCSV} disabled={exporting || matchedCount === 0}
                className="gap-2 bg-green-600 hover:bg-green-700" data-testid="export-csv-btn">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export CSV
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {entries.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-blue-600 font-medium">Total Angajați</p>
                <p className="text-2xl font-bold text-blue-900" data-testid="total-employees">{entries.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-green-600 font-medium">Potriviți (cu IBAN)</p>
                <p className="text-2xl font-bold text-green-900" data-testid="matched-count">{matchedCount}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-red-600 font-medium">Nepotriviți</p>
                <p className="text-2xl font-bold text-red-900" data-testid="unmatched-count">{unmatchedCount}</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-amber-600 font-medium">Total Plată</p>
                <p className="text-2xl font-bold text-amber-900" data-testid="total-amount">
                  {totalAmount.toLocaleString('ro-RO')} RON
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[60px] font-semibold">Nr. OP</TableHead>
                      <TableHead className="font-semibold">Nume (din PDF)</TableHead>
                      <TableHead className="font-semibold">Nume (din DB)</TableHead>
                      <TableHead className="font-semibold">CNP</TableHead>
                      <TableHead className="font-semibold">IBAN</TableHead>
                      <TableHead className="font-semibold w-[120px]">Sumă (RON)</TableHead>
                      <TableHead className="font-semibold w-[100px] text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry, index) => (
                      <TableRow key={index} className={!entry.matched ? 'bg-red-50/50' : ''} data-testid={`salary-row-${index}`}>
                        <TableCell className="font-mono text-sm">{entry.nr_op}</TableCell>
                        <TableCell className="font-medium text-sm">{entry.pdf_name}</TableCell>
                        <TableCell className="text-sm">{entry.db_name || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{entry.cnp || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {entry.iban ? (
                            <span className="text-green-700">{entry.iban}</span>
                          ) : entry.matched && entry.employee_id ? (
                            <IBANInlineEdit index={index} employeeId={entry.employee_id} onSave={handleSaveIBAN} />
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={entry.amount || ''} onChange={(e) => handleAmountChange(index, e.target.value)}
                            className="h-8 text-sm w-[110px]" data-testid={`amount-input-${index}`} />
                        </TableCell>
                        <TableCell className="text-center">
                          {entry.matched ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid={`status-matched-${index}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" />OK
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid={`status-unmatched-${index}`}>
                              <AlertCircle className="w-3 h-3 mr-1" />?
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {entries.length === 0 && !uploading && (
        <div className="text-center py-16 border rounded-lg bg-slate-50">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Nu există date încărcate</h3>
          <p className="text-slate-500 text-sm">
            Încarcă un PDF cu statul de plată pentru a genera fișierul CSV de plată bancară
          </p>
        </div>
      )}
    </div>
  );
});

// ============ Istoric Plăți Sub-component ============
const MONTH_NAMES = {
  '01': 'Ianuarie', '02': 'Februarie', '03': 'Martie', '04': 'Aprilie',
  '05': 'Mai', '06': 'Iunie', '07': 'Iulie', '08': 'August',
  '09': 'Septembrie', '10': 'Octombrie', '11': 'Noiembrie', '12': 'Decembrie'
};

const IstoricPlati = memo(function IstoricPlati({ token }) {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/exports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setExports(data.exports || []);
      }
    } catch {
      console.error('Error fetching exports');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchExports(); }, [fetchExports]);

  const handleExpand = useCallback(async (exportId) => {
    if (expandedId === exportId) {
      setExpandedId(null);
      setExpandedData(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/exports/${exportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setExpandedData(data);
        setExpandedId(exportId);
      }
    } catch {
      toast.error('Eroare la încărcarea detaliilor');
    }
  }, [expandedId, token]);

  const handleDownloadPdf = useCallback(async (exportId, pdfFilename) => {
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/exports/${exportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = pdfFilename || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Eroare la descărcarea PDF-ului');
    }
  }, [token]);

  const handleDelete = useCallback(async (exportId) => {
    if (!window.confirm('Sigur vrei să ștergi această înregistrare?')) return;
    setDeleting(exportId);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/exports/${exportId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setExports(prev => prev.filter(e => e.id !== exportId));
        if (expandedId === exportId) {
          setExpandedId(null);
          setExpandedData(null);
        }
        toast.success('Înregistrare ștearsă');
      } else {
        toast.error('Eroare la ștergere');
      }
    } catch {
      toast.error('Eroare de conexiune');
    } finally {
      setDeleting(null);
    }
  }, [token, expandedId]);

  const formatLuna = (luna) => {
    if (!luna) return '?';
    const [year, month] = luna.split('-');
    return `${MONTH_NAMES[month] || month} ${year}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (exports.length === 0 && !loading) {
    return (
      <div className="space-y-4">
        <LunaSelector month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} />
        <div className="text-center py-16 border rounded-lg bg-slate-50">
          <History className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Nu există exporturi salvate</h3>
          <p className="text-slate-500 text-sm">
            Exporturile CSV vor fi salvate automat aici după generare
          </p>
        </div>
      </div>
    );
  }

  // Filter by selected month
  const selectedLuna = `${year}-${String(month).padStart(2, '0')}`;
  const filteredExports = exports.filter(exp => exp.luna === selectedLuna);

  // Group by month (filtered)
  const grouped = {};
  for (const exp of filteredExports) {
    const key = exp.luna || 'necunoscut';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(exp);
  }

  return (
    <div className="space-y-4" data-testid="istoric-plati">
      <LunaSelector month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} />
      
      {filteredExports.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-slate-50">
          <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 text-sm">Nu există exporturi pentru {MONTHS_RO_LIST[month-1]?.label} {year}</p>
        </div>
      ) : (
      <>
      {Object.entries(grouped).map(([luna, items]) => (
        <Card key={luna}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              {formatLuna(luna)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {items.map((exp) => (
              <div key={exp.id} className="border rounded-lg overflow-hidden" data-testid={`export-${exp.id}`}>
                {/* Header row */}
                <div
                  className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleExpand(exp.id)}
                >
                  <div className="flex items-center gap-3">
                    {expandedId === exp.id
                      ? <ChevronDown className="w-4 h-4 text-slate-500" />
                      : <ChevronRight className="w-4 h-4 text-slate-500" />
                    }
                    <Badge className={exp.pdf_type === 'tichete'
                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-100'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-100'
                    }>
                      {exp.pdf_type === 'tichete' ? 'Clauza Mobilitate' : 'Salarii'}
                    </Badge>
                    <span className="text-sm font-medium text-slate-700">
                      {exp.employee_count} angajați
                    </span>
                    <span className="text-sm font-bold text-slate-900">
                      {(exp.total_sum || 0).toLocaleString('ro-RO')} RON
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{formatDate(exp.exported_at)}</span>
                    <Button variant="outline" size="sm" className="h-8 gap-1"
                      onClick={(e) => { e.stopPropagation(); handleDownloadPdf(exp.id, exp.pdf_filename); }}
                      data-testid={`download-pdf-${exp.id}`}>
                      <FileDown className="w-3 h-3" /> PDF
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); handleDelete(exp.id); }}
                      disabled={deleting === exp.id}
                      data-testid={`delete-export-${exp.id}`}>
                      {deleting === exp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>

                {/* Expanded detail */}
                {expandedId === exp.id && expandedData && (
                  <div className="p-3 border-t">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-white">
                          <TableHead className="font-semibold text-xs">Angajat</TableHead>
                          <TableHead className="font-semibold text-xs">CNP</TableHead>
                          <TableHead className="font-semibold text-xs">IBAN</TableHead>
                          <TableHead className="font-semibold text-xs text-right">Sumă (RON)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(expandedData.entries || []).map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm font-medium">{entry.db_name || entry.pdf_name}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.cnp || '-'}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.iban || '-'}</TableCell>
                            <TableCell className="text-sm font-bold text-right">
                              {(entry.amount || 0).toLocaleString('ro-RO')}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 font-bold">
                          <TableCell colSpan={3} className="text-sm">TOTAL</TableCell>
                          <TableCell className="text-sm text-right">
                            {(expandedData.total_sum || 0).toLocaleString('ro-RO')} RON
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
      </>
      )}
    </div>
  );
});

// ============ Cheltuieli Salariale Sub-component ============
const MONTH_LABELS = {
  '01': 'Ianuarie', '02': 'Februarie', '03': 'Martie', '04': 'Aprilie',
  '05': 'Mai', '06': 'Iunie', '07': 'Iulie', '08': 'August',
  '09': 'Septembrie', '10': 'Octombrie', '11': 'Noiembrie', '12': 'Decembrie'
};

const CheltuieliSalariale = memo(function CheltuieliSalariale({ token }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/salary-adapter/cheltuieli`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          setData(d.months || []);
        }
      } catch {
        console.error('Error fetching cheltuieli');
      } finally {
        setLoading(false);
      }
    };
    fetch_();
  }, [token]);

  const formatLuna = (luna) => {
    if (!luna) return '?';
    const [year, month] = luna.split('-');
    return `${MONTH_LABELS[month] || month} ${year}`;
  };

  const formatNum = (n) => (n || 0).toLocaleString('ro-RO');

  const handleExport = async (type) => {
    const luna = `${year}-${String(month).padStart(2, '0')}`;
    setExporting(type);
    try {
      const res = await fetch(`${API_URL}/api/salary-adapter/cheltuieli/export-${type}?luna=${luna}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const monthLabel = MONTHS_RO_LIST[month - 1]?.label || month;
        a.download = `Cheltuieli_Salariale_${monthLabel}_${year}.${type === 'pdf' ? 'pdf' : 'xlsx'}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${type.toUpperCase()} exportat`);
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Eroare export');
      }
    } catch { toast.error('Eroare de rețea'); }
    finally { setExporting(null); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <LunaSelector month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} />
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  // Filter by selected month
  const selectedLuna = `${year}-${String(month).padStart(2, '0')}`;
  const filteredData = data.filter(m => m.luna === selectedLuna);

  return (
    <div className="space-y-6" data-testid="cheltuieli-salariale">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <LunaSelector month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} />
        {filteredData.length > 0 && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} disabled={!!exporting} data-testid="export-cheltuieli-pdf">
              {exporting === 'pdf' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleExport('excel')} disabled={!!exporting} data-testid="export-cheltuieli-excel">
              {exporting === 'excel' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}Excel
            </Button>
          </div>
        )}
      </div>
      
      {filteredData.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-slate-50">
          <Calculator className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 text-sm">Nu există date pentru {MONTHS_RO_LIST[month-1]?.label} {year}</p>
          <p className="text-xs text-slate-400 mt-1">Exportă un stat de plată din "Încarcă Stat de Plată" pentru această lună</p>
        </div>
      ) : (
      <>
      {filteredData.map((monthData) => (
        <Card key={monthData.luna}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5 text-blue-600" />
                {formatLuna(monthData.luna)}
              </CardTitle>
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                {monthData.employee_count} angajați
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Table header */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-0 px-3 py-2 border-b text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <span>Angajat</span>
              <span className="text-right">Salariu net încasat</span>
              <span className="text-right">Tichete</span>
              <span className="text-right">Clauza</span>
              <span className="text-right">Total</span>
            </div>
            {monthData.employees.map((emp, idx) => {
              const empKey = `${monthData.luna}-${emp.cnp || idx}`;
              const isExpanded = expandedEmp === empKey;
              const sal = emp.salary || {};
              const tich = emp.tichete || {};
              const hasSalary = Object.keys(sal).length > 0;
              const hasTichete = Object.keys(tich).length > 0;
              const salVal = sal.rest_plata || 0;
              const tichVal = tich.tichete_total || 0;
              const clauzaVal = tich.clauza_total || 0;
              const totalVal = salVal + tichVal + clauzaVal;

              return (
                <div key={empKey} className="border-b last:border-b-0" data-testid={`chelt-emp-${idx}`}>
                  <div
                    className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-0 px-3 py-1.5 cursor-pointer hover:bg-slate-50 transition-colors items-center"
                    onClick={() => setExpandedEmp(isExpanded ? null : empKey)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isExpanded
                        ? <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />
                        : <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />
                      }
                      <span className="text-sm font-medium truncate">{emp.name}</span>
                    </div>
                    <span className="text-sm text-right tabular-nums">{salVal ? formatNum(salVal) : ''}</span>
                    <span className="text-sm text-right tabular-nums">{tichVal ? formatNum(tichVal) : ''}</span>
                    <span className="text-sm text-right tabular-nums">{clauzaVal ? formatNum(clauzaVal) : ''}</span>
                    <span className="text-sm font-semibold text-right tabular-nums">{totalVal ? formatNum(totalVal) : ''}</span>
                  </div>

                  {isExpanded && (
                    <div className="px-4 py-3 border-t bg-slate-50 space-y-3">
                      {hasSalary && (
                        <div>
                          <h4 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Din Stat de Plată</h4>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                            {sal.functia && (
                              <div><p className="text-[10px] text-slate-400">Funcția</p><p className="text-xs font-medium capitalize">{sal.functia}</p></div>
                            )}
                            <div><p className="text-[10px] text-slate-400">Salariu bază</p><p className="text-xs font-semibold">{formatNum(sal.salariu_baza)}</p></div>
                            <div><p className="text-[10px] text-slate-400">Sporuri</p><p className="text-xs">{formatNum(sal.sporuri)}</p></div>
                            <div><p className="text-[10px] text-slate-400">Zile lucrate</p><p className="text-xs">{sal.zile_lucrate || '-'}</p></div>
                            <div><p className="text-[10px] text-slate-400">Ore lucrate</p><p className="text-xs">{sal.ore_lucrate || '-'}</p></div>
                            <div><p className="text-[10px] text-slate-400">Ven. salariale</p><p className="text-xs">{formatNum(sal.venituri_salariale)}</p></div>
                            <div><p className="text-[10px] text-slate-400">Venit brut</p><p className="text-xs font-semibold">{formatNum(sal.venit_brut)}</p></div>
                            <div><p className="text-[10px] text-slate-400">CAS</p><p className="text-xs">{formatNum(sal.cas)}</p></div>
                            <div><p className="text-[10px] text-slate-400">CASS</p><p className="text-xs">{formatNum(sal.cass)}</p></div>
                            <div><p className="text-[10px] text-slate-400">Venit net</p><p className="text-xs">{formatNum(sal.venit_net)}</p></div>
                            <div><p className="text-[10px] text-slate-400">Ded. personală</p><p className="text-xs">{formatNum(sal.deducere_personala)}</p></div>
                            <div><p className="text-[10px] text-slate-400">Baza calcul</p><p className="text-xs">{formatNum(sal.baza_calcul)}</p></div>
                            {sal.deducere_suplimentara > 0 && (
                              <div><p className="text-[10px] text-slate-400">Ded. suplim.</p><p className="text-xs">{formatNum(sal.deducere_suplimentara)}</p></div>
                            )}
                            <div><p className="text-[10px] text-slate-400">Impozit</p><p className="text-xs">{formatNum(sal.impozit)}</p></div>
                            {sal.retineri > 0 && (
                              <div><p className="text-[10px] text-slate-400">Rețineri</p><p className="text-xs font-semibold">{formatNum(sal.retineri)}</p></div>
                            )}
                            <div><p className="text-[10px] text-slate-400">Salariu net fără taxe</p><p className="text-xs font-semibold">{formatNum(sal.salariu_net)}</p></div>
                            <div><p className="text-[10px] text-slate-400">Salariu net încasat</p><p className="text-xs font-semibold">{formatNum(sal.rest_plata)}</p></div>
                          </div>
                        </div>
                      )}

                      {hasTichete && (
                        <div>
                          <h4 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Din Situație Tichete</h4>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                            {tich.tichete_total > 0 && (
                              <>
                                <div><p className="text-[10px] text-slate-400">Tichete masă</p><p className="text-xs font-semibold">{formatNum(tich.tichete_total)}</p></div>
                                {tich.tichete_zile > 0 && (
                                  <div><p className="text-[10px] text-slate-400">Zile × Valoare</p><p className="text-xs">{tich.tichete_zile} × {tich.tichete_valoare}</p></div>
                                )}
                              </>
                            )}
                            {tich.clauza_total > 0 && (
                              <>
                                <div><p className="text-[10px] text-slate-400">Clauza mobilitate</p><p className="text-xs font-semibold">{formatNum(tich.clauza_total)}</p></div>
                                {tich.clauza_zile > 0 && (
                                  <div><p className="text-[10px] text-slate-400">Zile × Valoare</p><p className="text-xs">{tich.clauza_zile} × {tich.clauza_valoare}</p></div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {!hasSalary && !hasTichete && (
                        <p className="text-xs text-slate-400">Nu sunt date disponibile</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Totals row */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-0 px-3 py-2 border-t-2 border-slate-300 bg-slate-100 font-semibold text-sm">
              <span>TOTAL</span>
              <span className="text-right tabular-nums">
                {formatNum(monthData.employees.reduce((s, e) => s + (e.salary?.rest_plata || 0), 0))}
              </span>
              <span className="text-right tabular-nums">
                {formatNum(monthData.employees.reduce((s, e) => s + (e.tichete?.tichete_total || 0), 0))}
              </span>
              <span className="text-right tabular-nums">
                {formatNum(monthData.employees.reduce((s, e) => s + (e.tichete?.clauza_total || 0), 0))}
              </span>
              <span className="text-right tabular-nums font-bold">
                {formatNum(monthData.employees.reduce((s, e) => s + (e.salary?.rest_plata || 0) + (e.tichete?.tichete_total || 0) + (e.tichete?.clauza_total || 0), 0))}
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
      </>
      )}
    </div>
  );
});

// ============ Main Component ============
const AdaptareTab = memo(function AdaptareTab({ token }) {
  const [activeSubTab, setActiveSubTab] = useState('setari');

  const subTabs = [
    { id: 'setari', label: 'Setări Adaptare', icon: Settings },
    { id: 'upload', label: 'Încarcă Stat de Plată', icon: Upload },
    { id: 'istoric', label: 'Istoric Plăți', icon: History },
    { id: 'cheltuieli', label: 'Cheltuieli Salariale', icon: Calculator }
  ];

  return (
    <div className="space-y-4" data-testid="adaptare-tab">
      <div className="flex gap-2 border-b pb-2">
        {subTabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveSubTab(tab.id)} data-testid={`adaptare-subtab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors text-sm font-medium ${
              activeSubTab === tab.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'setari' && <SetariAdaptare token={token} />}
      {activeSubTab === 'upload' && <UploadStatPlata token={token} />}
      {activeSubTab === 'istoric' && <IstoricPlati token={token} />}
      {activeSubTab === 'cheltuieli' && <CheltuieliSalariale token={token} />}
    </div>
  );
});

export default AdaptareTab;
